import React, { useState, useEffect, useRef } from "react";
import { 
  Search, 
  Plus, 
  Edit2, 
  Trash2, 
  Smartphone, 
  RefreshCw, 
  Settings, 
  RotateCcw, 
  AlertTriangle, 
  X, 
  Calendar, 
  ShieldAlert, 
  FileSpreadsheet,
  CheckCircle2,
  ChevronLeft,
  ChevronRight
} from "lucide-react";

// Sử dụng đường dẫn import đồng bộ với dự án của bạn
import axios from "../api/axios";

const Devices = () => {
  const [devices, setDevices] = useState([]);
  const [deletedDevices, setDeletedDevices] = useState([]);
  const [activeTab, setActiveTab] = useState("active"); 
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10; 
  
  // Modal states
  const [showFormModal, setShowFormModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showHardDeleteModal, setShowHardDeleteModal] = useState(false);
  const [currentDevice, setCurrentDevice] = useState(null);
  const [formData, setFormData] = useState({ imei: "", active_date: "" });

  const fileInputRef = useRef(null);

  // Lấy danh sách thiết bị active
  const fetchActiveDevices = async () => {
    setLoading(true);
    try {
      const response = await axios.get("/devices/active");
      setDevices(response.data || []);
    } catch (err) {
      setError(err.response?.data?.detail || "Không thể tải danh sách hoạt động");
    } finally {
      setLoading(false);
    }
  };

  // Lấy danh sách thiết bị đã xóa
  const fetchDeletedDevices = async () => {
    setLoading(true);
    try {
      const response = await axios.get("/devices/deleted");
      setDeletedDevices(response.data || []);
    } catch (err) {
      setError(err.response?.data?.detail || "Không thể tải danh sách đã xóa");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActiveDevices();
    fetchDeletedDevices();
  }, []);

  // Reset trang khi đổi tab hoặc tìm kiếm
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, search]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === "active") fetchActiveDevices();
    else fetchDeletedDevices();
  };

  const formatDate = (dateString) => {
    if (!dateString) return "---";
    const date = new Date(dateString);
    return date.toLocaleString('vi-VN', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const openFormModal = (device = null) => {
    if (device) {
      setCurrentDevice(device);
      const date = device.active_date ? new Date(device.active_date).toISOString().slice(0, 16) : "";
      setFormData({ imei: device.imei, active_date: date });
    } else {
      setCurrentDevice(null);
      setFormData({ imei: "", active_date: new Date().toISOString().slice(0, 16) });
    }
    setShowFormModal(true);
  };

  const handleSave = async () => {
    if (!formData.imei) {
      setError("Vui lòng nhập số IMEI");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        imei: formData.imei,
        active_date: new Date(formData.active_date).toISOString()
      };
      
      if (currentDevice) {
        // Gửi yêu cầu cập nhật nếu có currentDevice
        await axios.put(`/devices/update/${currentDevice.imei}`, payload);
      } else {
        // Gửi yêu cầu tạo mới
        await axios.post("/devices/create", payload);
      }
      
      setSuccessMsg(currentDevice ? "Đã cập nhật thành công!" : "Đã thêm thiết bị thành công!");
      setShowFormModal(false);
      fetchActiveDevices();
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || "Không thể lưu thông tin thiết bị");
    } finally {
      setSubmitting(false);
    }
  };

  const handleImportExcel = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setSubmitting(true);
    setError(null);
    const form = new FormData();
    form.append("file", file);

    try {
      const response = await axios.post("/devices/import", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSuccessMsg(response.data.detail || "Nhập dữ liệu thành công!");
      fetchActiveDevices();
      setTimeout(() => setSuccessMsg(null), 5000);
    } catch (err) {
      setError("Lỗi khi nhập tệp Excel");
    } finally {
      setSubmitting(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleSoftDelete = async () => {
    setSubmitting(true);
    try {
      await axios.put(`/devices/soft-delete/${currentDevice.imei}`);
      setShowDeleteModal(false);
      fetchActiveDevices();
      fetchDeletedDevices();
      setSuccessMsg(`Đã tạm xóa thiết bị ${currentDevice.imei}`);
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (err) {
      setError("Không thể xóa thiết bị");
    } finally {
      setSubmitting(false);
    }
  };

  const handleHardDelete = async () => {
    setSubmitting(true);
    try {
      await axios.delete(`/devices/remove/${currentDevice.imei}`);
      setShowHardDeleteModal(false);
      fetchDeletedDevices();
      setSuccessMsg(`Đã xóa vĩnh viễn thiết bị ${currentDevice.imei}`);
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (err) {
      setError("Không thể xóa vĩnh viễn thiết bị");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRestore = async (device) => {
    setLoading(true);
    try {
      await axios.put(`/devices/restore/${device.imei}`);
      fetchActiveDevices();
      fetchDeletedDevices();
      setSuccessMsg(`Đã khôi phục thiết bị ${device.imei}`);
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (err) {
      setError("Không thể khôi phục thiết bị");
    } finally {
      setLoading(false);
    }
  };

  // Logic Phân Trang
  const listToDisplay = activeTab === "active" ? devices : deletedDevices;
  const filteredList = listToDisplay.filter(d => 
    d.imei?.toLowerCase().includes(search.toLowerCase())
  );

  const totalPages = Math.ceil(filteredList.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredList.slice(indexOfFirstItem, indexOfLastItem);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <div className="min-h-screen bg-gray-50 text-gray-800 p-2 md:p-4 lg:p-6 font-sans">
      <div className="max-w-[98%] mx-auto">
        
        {/* Header Section */}
        <div className="mb-6 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 bg-white p-6 rounded-3xl shadow-sm border border-gray-100">
          <div>
            <h1 className="text-2xl font-black text-gray-900 tracking-tight flex items-center gap-3">
              <Settings className="text-blue-600" size={28} />
              Quản Lý Thiết Bị GPS
            </h1>
            <p className="text-gray-500 text-sm font-medium italic tracking-tight">Cấu hình hệ thống thiết bị</p>
          </div>
          
          <div className="flex flex-wrap gap-3">
            <button 
              onClick={() => activeTab === "active" ? fetchActiveDevices() : fetchDeletedDevices()} 
              disabled={loading}
              className="flex items-center justify-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 px-5 py-3 rounded-2xl font-bold transition-all disabled:opacity-50"
            >
              <RefreshCw size={18} className={loading ? "animate-spin" : ""} /> LÀM MỚI
            </button>
            
            <input 
              type="file" 
              ref={fileInputRef} 
              onChange={handleImportExcel} 
              accept=".xlsx, .xls, .csv" 
              className="hidden" 
            />
            <button 
              onClick={() => fileInputRef.current?.click()}
              disabled={submitting}
              className="flex items-center justify-center gap-2 bg-emerald-50 text-emerald-700 border border-emerald-100 hover:bg-emerald-100 px-5 py-3 rounded-2xl font-bold transition-all disabled:opacity-50"
            >
              <FileSpreadsheet size={20} /> NHẬP EXCEL
            </button>

            <button 
              onClick={() => openFormModal()}
              className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-2xl font-bold shadow-xl shadow-blue-100 transition-all active:scale-95"
            >
              <Plus size={20} /> THÊM MỚI
            </button>
          </div>
        </div>

        {/* Global Messages */}
        {successMsg && (
          <div className="mb-6 p-4 bg-emerald-50 border border-emerald-100 rounded-2xl flex justify-between items-center text-emerald-700 font-bold animate-in fade-in slide-in-from-top-4">
            <div className="flex items-center gap-3">
              <CheckCircle2 size={20} />
              <span>{successMsg}</span>
            </div>
            <button onClick={() => setSuccessMsg(null)}><X size={18}/></button>
          </div>
        )}

        {error && (
          <div className="mb-6 p-4 bg-rose-50 border border-rose-100 rounded-2xl flex justify-between items-center text-rose-600 font-bold animate-in fade-in slide-in-from-top-4">
            <div className="flex items-center gap-3">
              <AlertTriangle size={20} />
              <span>Lỗi: {error}</span>
            </div>
            <button onClick={() => setError(null)}><X size={18}/></button>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="flex gap-4 mb-6 border-b border-gray-200 px-2">
          <button 
            onClick={() => handleTabChange("active")}
            className={`pb-4 px-4 text-sm font-black transition-all relative ${activeTab === "active" ? "text-blue-600" : "text-gray-400 hover:text-gray-600"}`}
          >
            ĐANG HOẠT ĐỘNG ({devices.length})
            {activeTab === "active" && <div className="absolute bottom-0 left-0 w-full h-1 bg-blue-600 rounded-t-full"></div>}
          </button>
          <button 
            onClick={() => handleTabChange("deleted")}
            className={`pb-4 px-4 text-sm font-black transition-all relative ${activeTab === "deleted" ? "text-rose-600" : "text-gray-400 hover:text-gray-600"}`}
          >
            DANH SÁCH ĐÃ XÓA ({deletedDevices.length})
            {activeTab === "deleted" && <div className="absolute bottom-0 left-0 w-full h-1 bg-rose-600 rounded-t-full"></div>}
          </button>
        </div>

        {/* Search */}
        <div className="mb-6 relative">
          <span className="absolute inset-y-0 left-0 pl-5 flex items-center text-gray-400">
            <Search size={20} />
          </span>
          <input 
            type="text" 
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={`Tìm theo IMEI trong danh sách ${activeTab === "active" ? "hoạt động" : "đã xóa"}...`} 
            className="block w-full pl-14 pr-4 py-4 border border-gray-200 rounded-2xl bg-white shadow-sm focus:ring-2 focus:ring-blue-500 outline-none transition-all font-medium"
          />
        </div>

        {/* Table Content */}
        <div className="bg-white shadow-sm rounded-3xl overflow-hidden border border-gray-200">
          <div className="overflow-x-auto">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-32">
                <RefreshCw size={48} className="animate-spin text-blue-500 mb-4 opacity-50" />
                <p className="font-black text-gray-400 uppercase text-xs tracking-widest">Đang tải dữ liệu...</p>
              </div>
            ) : (
              <>
                <table className="min-w-full divide-y divide-gray-100">
                  <thead className="bg-gray-50/50">
                    <tr>
                      <th className="px-8 py-5 text-left text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Thiết bị</th>
                      <th className="px-8 py-5 text-left text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Ngày kích hoạt</th>
                      <th className="px-8 py-5 text-left text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Cập nhật</th>
                      <th className="px-8 py-5 text-center text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Trạng thái</th>
                      <th className="px-8 py-5 text-right text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Hành động</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-50">
                    {currentItems.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="px-8 py-20 text-center text-gray-400">
                          <Smartphone size={40} className="mb-3 opacity-20 mx-auto" />
                          <p className="font-bold text-sm">Không tìm thấy thiết bị nào</p>
                        </td>
                      </tr>
                    ) : currentItems.map((device, idx) => (
                      <tr key={device.imei + idx} className="hover:bg-blue-50/20 transition-all group">
                        <td className="px-8 py-5">
                          <div className="flex items-center gap-4">
                            <div className={`p-3 rounded-xl ${activeTab === "active" ? "bg-blue-50 text-blue-600" : "bg-gray-100 text-gray-400"}`}>
                              <Smartphone size={20} />
                            </div>
                            <div>
                              <p className="text-sm font-black text-gray-900 leading-none mb-1 uppercase tracking-tight">{device.imei}</p>
                              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">GPS Tracker</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-8 py-5 font-bold text-gray-700 text-sm">
                          {formatDate(device.active_date)}
                        </td>
                        <td className="px-8 py-5 text-xs font-medium text-gray-500">
                           {formatDate(device.updated_at || device.created_at)}
                        </td>
                        <td className="px-8 py-5 text-center">
                          {device.is_deleted === 0 || !device.is_deleted ? (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-[10px] font-black bg-emerald-50 text-emerald-600 ring-1 ring-emerald-100 uppercase">
                              ACTIVE
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-[10px] font-black bg-rose-50 text-rose-600 ring-1 ring-rose-100 uppercase">
                              DELETED
                            </span>
                          )}
                        </td>
                        <td className="px-8 py-5 text-right">
                          {activeTab === "active" ? (
                            <div className="flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button onClick={() => openFormModal(device)} className="p-2 text-gray-400 hover:text-blue-600 hover:bg-white rounded-lg shadow-sm border border-transparent hover:border-blue-100 transition-all">
                                <Edit2 size={16} />
                              </button>
                              <button onClick={() => { setCurrentDevice(device); setShowDeleteModal(true); }} className="p-2 text-gray-400 hover:text-rose-600 hover:bg-white rounded-lg shadow-sm border border-transparent hover:border-rose-100 transition-all">
                                <Trash2 size={16} />
                              </button>
                            </div>
                          ) : (
                            <div className="flex justify-end gap-2">
                              <button 
                                onClick={() => handleRestore(device)} 
                                className="flex items-center gap-1 text-[10px] font-black text-blue-600 bg-blue-50 px-3 py-1.5 rounded-lg hover:bg-blue-600 hover:text-white transition-all uppercase shadow-sm"
                              >
                                <RotateCcw size={12} /> Khôi phục
                              </button>
                              <button 
                                onClick={() => { setCurrentDevice(device); setShowHardDeleteModal(true); }}
                                className="p-1.5 text-rose-600 bg-rose-50 rounded-lg hover:bg-rose-600 hover:text-white transition-all shadow-sm"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <div className="px-8 py-6 bg-gray-50/30 border-t border-gray-100 flex items-center justify-between">
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">
                      Hiển thị {indexOfFirstItem + 1} - {Math.min(indexOfLastItem, filteredList.length)} trên tổng số {filteredList.length}
                    </p>
                    <div className="flex items-center gap-1">
                      <button 
                        onClick={() => paginate(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="p-2 rounded-xl border border-gray-200 text-gray-400 hover:bg-white hover:text-blue-600 disabled:opacity-30 disabled:hover:bg-transparent transition-all"
                      >
                        <ChevronLeft size={20} />
                      </button>
                      
                      {[...Array(totalPages)].map((_, i) => (
                        <button
                          key={i + 1}
                          onClick={() => paginate(i + 1)}
                          className={`w-10 h-10 rounded-xl text-xs font-black transition-all ${
                            currentPage === i + 1 
                              ? "bg-blue-600 text-white shadow-lg shadow-blue-100" 
                              : "text-gray-400 hover:bg-white hover:text-gray-900 border border-transparent hover:border-gray-200"
                          }`}
                        >
                          {i + 1}
                        </button>
                      ))}

                      <button 
                        onClick={() => paginate(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="p-2 rounded-xl border border-gray-200 text-gray-400 hover:bg-white hover:text-blue-600 disabled:opacity-30 disabled:hover:bg-transparent transition-all"
                      >
                        <ChevronRight size={20} />
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* MODAL: Add / Edit */}
      {showFormModal && (
        <div className="fixed inset-0 bg-gray-950/80 backdrop-blur-md flex justify-center items-center z-50 p-4">
          <div className="bg-white rounded-[2.5rem] shadow-2xl max-w-xl w-full overflow-hidden animate-in fade-in zoom-in duration-300">
            <div className="px-10 py-8 border-b border-gray-100 flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-black text-gray-900 tracking-tight">
                  {currentDevice ? "Chỉnh sửa thiết bị" : "Thêm thiết bị mới"}
                </h2>
                <p className="text-sm font-medium text-gray-500 uppercase tracking-tighter">Nhập thông tin phần cứng</p>
              </div>
              <button onClick={() => setShowFormModal(false)} className="p-2 hover:bg-gray-100 rounded-full text-gray-400"><X /></button>
            </div>
            
            <div className="p-10 space-y-6">
              <div>
                <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">Số IMEI</label>
                <div className="relative">
                  <Smartphone className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" size={20} />
                  <input 
                    type="text" 
                    value={formData.imei}
                    disabled={currentDevice !== null}
                    onChange={(e) => setFormData({...formData, imei: e.target.value})}
                    className="w-full pl-12 pr-5 py-4 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-blue-500 outline-none font-mono font-bold text-gray-700 disabled:bg-gray-100" 
                    placeholder="Ví dụ: 029057706866" 
                  />
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">Ngày kích hoạt</label>
                <div className="relative">
                  <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" size={20} />
                  <input 
                    type="datetime-local" 
                    value={formData.active_date}
                    onChange={(e) => setFormData({...formData, active_date: e.target.value})}
                    className="w-full pl-12 pr-5 py-4 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-blue-500 outline-none font-bold text-gray-700" 
                  />
                </div>
              </div>
            </div>
            
            <div className="px-10 py-8 bg-gray-50 flex gap-4">
              <button onClick={() => setShowFormModal(false)} className="flex-1 py-4 text-xs font-black text-gray-400 hover:text-gray-600 uppercase tracking-widest">Hủy</button>
              <button onClick={handleSave} disabled={submitting} className="flex-2 px-12 py-4 bg-blue-600 text-white font-black rounded-2xl shadow-xl hover:bg-blue-700 transition-all uppercase text-xs tracking-widest disabled:opacity-50">
                {submitting ? "ĐANG XỬ LÝ..." : "XÁC NHẬN LƯU"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: Soft Delete */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-gray-950/90 backdrop-blur-sm flex justify-center items-center z-[60] p-4">
          <div className="bg-white rounded-[2rem] shadow-2xl max-w-sm w-full overflow-hidden animate-in zoom-in duration-200">
            <div className="p-8 text-center">
              <div className="w-20 h-20 bg-rose-50 text-rose-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <AlertTriangle size={40} />
              </div>
              <h3 className="text-xl font-black text-gray-900 mb-2">Xác nhận xóa?</h3>
              <p className="text-gray-500 text-sm font-medium px-4 leading-relaxed">
                Thiết bị <span className="font-bold text-gray-900">{currentDevice?.imei}</span> sẽ được chuyển vào danh sách đã xóa.
              </p>
            </div>
            <div className="p-6 bg-gray-50 flex gap-3">
              <button onClick={() => setShowDeleteModal(false)} className="flex-1 py-3 font-black text-gray-400 text-xs uppercase tracking-widest hover:text-gray-600">Hủy</button>
              <button onClick={handleSoftDelete} disabled={submitting} className="flex-1 py-3 bg-rose-600 text-white font-black rounded-xl text-xs uppercase tracking-widest hover:bg-rose-700 disabled:opacity-50">
                ĐỒNG Ý
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: Hard Delete */}
      {showHardDeleteModal && (
        <div className="fixed inset-0 bg-gray-950/95 backdrop-blur-md flex justify-center items-center z-[70] p-4">
          <div className="bg-white rounded-[2.5rem] shadow-2xl max-sm w-full overflow-hidden animate-in zoom-in duration-200 border border-rose-100">
            <div className="p-10 text-center">
              <div className="w-24 h-24 bg-rose-600 text-white rounded-full flex items-center justify-center mx-auto mb-8 shadow-2xl shadow-rose-200 animate-pulse">
                <ShieldAlert size={48} />
              </div>
              <h3 className="text-2xl font-black text-rose-600 mb-4 uppercase tracking-tighter">XÓA VĨNH VIỄN!</h3>
              <p className="text-gray-500 text-sm font-medium px-2 leading-relaxed">
                Cảnh báo: IMEI <span className="font-bold text-gray-900">{currentDevice?.imei}</span> sẽ bị xóa hoàn toàn khỏi hệ thống. Thao tác này không thể hoàn tác.
              </p>
            </div>
            <div className="p-8 bg-rose-50/50 flex flex-col gap-3">
              <button 
                onClick={handleHardDelete} 
                disabled={submitting}
                className="w-full py-4 bg-rose-600 text-white font-black rounded-2xl text-xs uppercase tracking-widest shadow-xl shadow-rose-200 hover:bg-rose-700 active:scale-95 disabled:opacity-50"
              >
                {submitting ? "ĐANG XÓA..." : "XÓA VĨNH VIỄN"}
              </button>
              <button 
                onClick={() => setShowHardDeleteModal(false)} 
                className="w-full py-3 font-black text-gray-400 text-[10px] uppercase tracking-[0.2em] hover:text-gray-600"
              >
                Hủy bỏ
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Devices;